import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CenterFocusWeakIcon from '@mui/icons-material/CenterFocusWeak';
import DocumentScannerSharpIcon from '@mui/icons-material/DocumentScannerSharp';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import CategoryIcon from '@mui/icons-material/Category';

import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { RESPONSIVE_DRAWER_WIDTH } from '../Constants';
import { useUserContext } from '../UserContext';
import { ListItemLink } from './ListItemLink';
import { isDocumentationEnabled, isLiveEnabled } from '../utils/navigationUtils';

export function MenuBar() {
  const { t } = useTranslation();
  const { data: user } = useUserContext();

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  // Server side logout: ends the Zitadel session, not just the local one.
  const { signoutRedirect } = useAuth();
  const logout = () => void signoutRedirect();

  const drawer = (
    <Box>
      <Toolbar sx={{ display: 'flex', justifyContent: 'center' }}>
        <img src='/logo_grey.png' alt='Optocycle Logo' style={{ height: '35px' }} />
      </Toolbar>
      <List className='list' sx={{ width: '100%' }}>
        {user?.preferredJobsite?.type === 'heap' && (
          <ListItem disablePadding>
            <ListItemLink
              to='/heap-recordings'
              primary={t('menubar.heapRecordings')}
              icon={<DocumentScannerSharpIcon />}
            />
          </ListItem>
        )}
        {user?.preferredJobsite?.type === 'heap' && isLiveEnabled(user.preferredJobsite) && (
          <ListItem disablePadding>
            <ListItemLink to='/heap-live' primary={t('menubar.heapLiveView')} icon={<CenterFocusWeakIcon />} />
          </ListItem>
        )}
        {user?.preferredJobsite?.type === 'truck' && isDocumentationEnabled(user.preferredJobsite) && (
          <ListItem disablePadding>
            <ListItemLink
              to='/documentation'
              primary={t('menubar.documentation')}
              icon={<DocumentScannerSharpIcon />}
            />
          </ListItem>
        )}
        {user?.preferredJobsite?.type !== 'heap' && (
          <ListItem disablePadding>
            <ListItemLink to='/reports' primary={t('menubar.reports')} icon={<AutoAwesomeIcon />} />
          </ListItem>
        )}
        {user?.preferredJobsite?.type === 'truck' && (
          <ListItem disablePadding>
            <ListItemLink to='/live-prediction' primary={t('menubar.livePrediction')} icon={<CenterFocusWeakIcon />} />
          </ListItem>
        )}
        {user?.preferredJobsite?.type === 'truck' && (
          <ListItem disablePadding>
            <ListItemLink to='/billable-materials' primary={t('menubar.billableMaterial')} icon={<CategoryIcon />} />
          </ListItem>
        )}
        {/* TODO: at the moment this link points to an initial jobsite selection. in the future it should point to the jobsite management */}
        <ListItem disablePadding>
          <ListItemLink to='/jobsite-select' primary={t('menubar.jobsiteSelect')} icon={<LocationOnIcon />} />
        </ListItem>
        <ListItem disablePadding>
          <ListItemLink to='/user-admin' primary={t('menubar.userAdmin')} icon={<PersonIcon />} />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box>
      <AppBar position='fixed'>
        <Toolbar>
          <IconButton color='inherit' aria-label='open drawer' edge='start' onClick={handleDrawerToggle} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
          <Typography variant='h6' noWrap component='div' sx={{ flexGrow: 1 }}>
            {user?.organisation?.name}
          </Typography>
          <Typography variant='h6' noWrap component='div' sx={{ flexGrow: 1 }}>
            {user?.preferredJobsite?.name}
          </Typography>

          <Typography variant='subtitle1'>{user?.displayName}</Typography>
          <Box sx={{}}>
            <Tooltip title={t('menubar.settings')}>
              <IconButton onClick={handleOpenUserMenu}>
                <AccountCircleIcon htmlColor='white' fontSize='large' />
              </IconButton>
            </Tooltip>
            <Menu
              id='menu-appbar'
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem key='logout' onClick={logout}>
                <Typography textAlign='center'>Logout</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant='temporary'
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { lg: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: RESPONSIVE_DRAWER_WIDTH },
        }}
      >
        <Toolbar>
          <IconButton
            color='inherit'
            aria-label='open drawer'
            edge='start'
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { lg: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant='h6' noWrap component='div' sx={{ flexGrow: 1 }}>
            {user?.organisation?.name}
          </Typography>
          <Typography variant='subtitle1'>{user?.displayName}</Typography>
          <Box>
            <Tooltip title={t('menubar.settings')}>
              <IconButton onClick={handleOpenUserMenu}>
                <AccountCircleIcon htmlColor='white' fontSize='large' />
              </IconButton>
            </Tooltip>
            <Menu
              id='menu-appbar'
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem key='logout' onClick={logout}>
                <Typography textAlign='center'>{t('menubar.logout')}</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </Drawer>
      <Drawer
        variant='temporary'
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: RESPONSIVE_DRAWER_WIDTH },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
}
